import Phaser from 'phaser';
import {
  findFurniture,
  parseScooterFurnitureId,
  scooterFurnitureId,
  SCOOTER_MOUNT_DISTANCE,
  type ScooterDirection,
  type ScooterModel,
  type ScooterSnapshot,
} from '@campus/core';
import { ensureFurnitureTextures, spawnFurnitureImage } from '../loaders/FurnitureTextures.js';

/**
 * Trottinettes de la carte (voir @campus/core/scooters) :
 * - trottinette posée par l'admin = meuble `pl_trottinette_*` ; elle disparaît
 *   (sprite caché, corps solides coupés) dès que la room la signale déplacée
 *   ou prise ;
 * - trottinette reposée ailleurs : sprite propre, sans collision, à la
 *   position diffusée par le serveur ;
 * - la room ne garde que les trottinettes déplacées ou prises : absente de
 *   l'état = à sa place d'origine.
 * Purement de l'affichage : monter et descendre passent par le serveur.
 */

interface PlacedScooter {
  itemId: string;
  model: ScooterModel;
  direction: ScooterDirection;
  /** Milieu du bas de la case, comme ScooterSnapshot. */
  x: number;
  y: number;
  sprite: Phaser.GameObjects.Image;
  bodies: Phaser.Physics.Arcade.Image[];
}

export interface ScooterCandidate {
  itemId: string;
  model: ScooterModel;
  direction: ScooterDirection;
  x: number;
  y: number;
}

/** Marge sous la distance vérifiée par le serveur, pour qu'une demande affichée ne soit pas refusée. */
const CLIENT_MOUNT_DISTANCE = SCOOTER_MOUNT_DISTANCE - 8;

export class Scooters {
  private placed = new Map<string, PlacedScooter>();
  private state = new Map<string, ScooterSnapshot>();
  private parked = new Map<string, Phaser.GameObjects.Image>();
  /** Compteur de génération par trottinette : un chargement de texture dépassé n'affiche rien. */
  private generations = new Map<string, number>();

  constructor(private readonly scene: Phaser.Scene, private readonly tile: number) {}

  /** Mobilier (re)chargé : retenir les trottinettes posées par l'admin. */
  setPlaced(items: Array<{ id: string; item: { furnitureId: string; tileX: number; tileY: number }; sprite: Phaser.GameObjects.Image; bodies: Phaser.Physics.Arcade.Image[] }>): void {
    this.placed.clear();
    for (const f of items) {
      const parsed = parseScooterFurnitureId(f.item.furnitureId);
      if (!parsed) continue;
      this.placed.set(f.id, {
        itemId: f.id, ...parsed,
        x: f.item.tileX * this.tile + this.tile / 2,
        y: (f.item.tileY + 1) * this.tile,
        sprite: f.sprite, bodies: f.bodies,
      });
    }
    this.refreshAll();
  }

  setState(list: ScooterSnapshot[]): void {
    this.state = new Map(list.map((s) => [s.itemId, s]));
    this.refreshAll();
  }

  update(s: ScooterSnapshot): void {
    this.state.set(s.itemId, s);
    this.refresh(s.itemId);
  }

  /** Trottinette libre la plus proche de ce point (pieds de l'avatar), à portée. */
  nearest(x: number, y: number): ScooterCandidate | null {
    let best: ScooterCandidate | null = null;
    let bestDist = CLIENT_MOUNT_DISTANCE;
    const consider = (c: ScooterCandidate) => {
      const d = Math.hypot(c.x - x, c.y - y);
      if (d <= bestDist) { best = c; bestDist = d; }
    };
    for (const p of this.placed.values()) {
      if (!this.state.has(p.itemId)) consider(p);
    }
    for (const s of this.state.values()) {
      if (!s.riderUserId) consider(s);
    }
    return best;
  }

  /** Point (monde) au-dessus duquel placer l'invite pour cette trottinette. */
  promptAnchor(c: ScooterCandidate): { x: number; y: number } {
    return { x: c.x, y: c.y - this.tile };
  }

  destroy(): void {
    for (const img of this.parked.values()) img.destroy();
    this.parked.clear();
    this.placed.clear();
    this.state.clear();
  }

  private refreshAll(): void {
    const ids = new Set([...this.placed.keys(), ...this.state.keys(), ...this.parked.keys()]);
    for (const id of ids) this.refresh(id);
  }

  private refresh(itemId: string): void {
    const placed = this.placed.get(itemId);
    const st = this.state.get(itemId);
    const atOrigin = !st || (!st.riderUserId && !!placed && st.x === placed.x && st.y === placed.y && st.direction === placed.direction);
    if (placed) {
      placed.sprite.setVisible(atOrigin);
      for (const b of placed.bodies) {
        if (b.body) (b.body as Phaser.Physics.Arcade.StaticBody).enable = atOrigin;
      }
    }

    const generation = (this.generations.get(itemId) ?? 0) + 1;
    this.generations.set(itemId, generation);
    this.parked.get(itemId)?.destroy();
    this.parked.delete(itemId);
    if (!st || st.riderUserId || atOrigin) return;

    const entry = findFurniture(scooterFurnitureId(st.model, st.direction));
    if (!entry) return;
    void ensureFurnitureTextures(this.scene, [entry]).then(() => {
      if (this.generations.get(itemId) !== generation || !this.scene.sys?.displayList) return;
      const img = spawnFurnitureImage(this.scene, entry, st.x - this.tile / 2, st.y);
      img.setDepth(100 + st.y / 10);
      this.parked.set(itemId, img);
    });
  }
}
